import { Button, LoadingIndicator } from '@commenda/kakashi';
import { Icons } from '@commenda/kakashi/src/components/Icons';
import {
  RadioGroup,
  RadioGroupItem,
} from '@commenda/kakashi/src/components/RadioGroup';
import { useContext, useId, useMemo } from 'react';
import { useContentLibraryOptions } from '../../hooks/useContentLibraryOptions';
import { useFormFieldOptions } from '../../hooks/useFormFieldOptions';
import { FormContext } from '../../hooks/FormContext';
import {
  findMissingRequiredContentLibraryInput,
  hasMissingRequiredContentLibraryInputs,
} from '../../utils/context-requirements';
import { isRuleActive } from '../../utils/validation';
import type { DataSourceConfig, ValidationRules } from '@commenda/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

interface RadioOption {
  value: string;
  label: string;
  icon?: string;
  helpText?: string;
  disabled?: boolean;
}

export const RadioGroupComponent = ({
  fieldId,
  label,
  description,
  options: staticOptions,
  defaultValue,
  guidance,
  validation,
  props,
  dataSource,
}: {
  fieldId: string;
  label: string;
  description?: string;
  options?: RadioOption[];
  defaultValue?: string;
  guidance?: { text?: string };
  validation?: {
    rules?: ValidationRules;
    triggers?: string[];
  };
  props?: Record<string, unknown>;
  dataSource?: DataSourceConfig;
}) => {
  const {
    formData,
    updateField,
    validateField,
    errors,
    dslContext,
    isFieldVisible,
  } = useContext(FormContext);

  const reactId = useId();

  const isDynamicDataSource =
    dataSource?.type === 'content-library' && dataSource.contentLibrary;

  const isFormFieldDataSource =
    dataSource?.type === 'form-field' && !!dataSource.formField;

  const { options: formFieldOptions } = useFormFieldOptions({
    config: dataSource?.formField,
    formData,
    skip: !isFormFieldDataSource,
  });

  const {
    options: dynamicOptions,
    isLoading,
    isLoadingMore,
    error: optionsError,
    hasMore,
    loadMore,
  } = useContentLibraryOptions({
    config: dataSource?.contentLibrary,
    dslContext,
    formData,
    skip: !isDynamicDataSource,
  });

  const hasMissingInputs = useMemo(() => {
    if (!isDynamicDataSource) {
      return false;
    }
    return hasMissingRequiredContentLibraryInputs(
      dataSource?.contentLibrary?.inputs,
      dslContext,
      formData
    );
  }, [
    isDynamicDataSource,
    dataSource?.contentLibrary?.inputs,
    dslContext,
    formData,
  ]);

  const options: RadioOption[] = isFormFieldDataSource
    ? (formFieldOptions as RadioOption[])
    : isDynamicDataSource
      ? (dynamicOptions as RadioOption[])
      : staticOptions || [];
  const value = (formData[fieldId] as string) || defaultValue || '';

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);
  const iconMap = {
    building: Icons.Building01,
    home: Icons.Home02,
  };

  // Get layout preference from props, default to 'vertical'
  const radioLayout =
    (props?.radioLayout as 'horizontal' | 'vertical') || 'vertical';

  const handleChange = (val: string) => {
    updateField(fieldId, val);
    if (validation?.rules) {
      validateField(fieldId, val, validation.rules);
    }
  };

  // Determine container class based on layout
  const containerClass =
    radioLayout === 'horizontal'
      ? 'flex flex-row gap-4 flex-wrap'
      : 'flex flex-col space-y-2';

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 py-2 text-text-tertiary">
          <LoadingIndicator size={16} />
          <span className="txt-small-regular">Loading options...</span>
        </div>
      );
    }

    if (hasMissingInputs) {
      const missingInput = findMissingRequiredContentLibraryInput(
        dataSource?.contentLibrary?.inputs,
        dslContext,
        formData
      );
      const missingLabel =
        missingInput?.source === 'field' && missingInput.fieldId
          ? `Select ${missingInput.fieldId} first`
          : 'Waiting for required input...';

      return (
        <div className="py-2 text-text-tertiary txt-small-regular">
          {missingLabel}
        </div>
      );
    }

    if (optionsError) {
      return (
        <div className="py-2 text-text-error txt-small-regular">
          {optionsError.message}
        </div>
      );
    }

    if (options.length === 0) {
      return (
        <div className="py-2 text-text-tertiary txt-small-regular">
          No options available
        </div>
      );
    }

    return (
      <>
        <RadioGroup value={value} onValueChange={handleChange}>
          <div className={containerClass}>
            {options.map((option) => {
              const Icon = option.icon ? iconMap[option.icon] : null;
              return (
                <div
                  key={option.value}
                  className="flex items-start justify-center gap-1"
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`${reactId}-${option.value}`}
                    className="mt-1"
                    disabled={option.disabled}
                  />
                  <label
                    htmlFor={`${reactId}-${option.value}`}
                    className={`ml-3 flex-1 ${option.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-2">
                      {Icon && (
                        <Icon className="w-4 h-4 text-text-quaternary" />
                      )}
                      <span className="txt-small-medium text-text-secondary">
                        {option.label}
                      </span>
                    </div>
                    {option.helpText && (
                      <p className="txt-small-regular text-text-tertiary mt-1">
                        {option.helpText}
                      </p>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </RadioGroup>
        {isDynamicDataSource && hasMore && (
          <div className="mt-2">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={loadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <div className="flex items-center gap-2">
                  <LoadingIndicator size={16} />
                  <span>Loading...</span>
                </div>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={description}
        />
      )}
      {renderContent()}
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
